import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { STORAGE_KEYS } from '@/constants/app'
import {
  applyThemeToDocument,
  type ThemeMode,
  type ThemePalette,
  withThemeTransition,
} from '@/utils/theme-color'

interface UiState {
  sidebarCollapsed: boolean
  theme: ThemeMode
  /** System theme — Arctic Frost (default) or Deep Navy; each has light + dark. */
  palette: ThemePalette
  /** Dense tables / compact controls preference */
  density: 'comfortable' | 'compact'
  setSidebarCollapsed: (collapsed: boolean) => void
  toggleSidebar: () => void
  setTheme: (theme: ThemeMode) => void
  toggleTheme: () => void
  setPalette: (palette: ThemePalette) => void
  setDensity: (density: 'comfortable' | 'compact') => void
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      sidebarCollapsed: false,
      theme: 'light',
      palette: 'arctic',
      density: 'comfortable',

      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      toggleSidebar: () =>
        set({ sidebarCollapsed: !get().sidebarCollapsed }),
      setTheme: (theme) => {
        const { palette } = get()
        withThemeTransition(() => applyThemeToDocument(theme, palette))
        set({ theme })
      },
      toggleTheme: () => {
        const { theme, palette } = get()
        const next = theme === 'light' ? 'dark' : 'light'
        withThemeTransition(() => applyThemeToDocument(next, palette))
        set({ theme: next })
      },
      setPalette: (palette) => {
        const { theme } = get()
        withThemeTransition(() => applyThemeToDocument(theme, palette))
        set({ palette })
      },
      setDensity: (density) => set({ density }),
    }),
    {
      name: STORAGE_KEYS.THEME,
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
        palette: state.palette,
        density: state.density,
      }),
      onRehydrateStorage: () => (state) => {
        applyThemeToDocument(
          state?.theme === 'dark' ? 'dark' : 'light',
          state?.palette === 'navy' ? 'navy' : 'arctic',
        )
      },
    },
  ),
)
